import React, { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getOptimizedImageUrl, getImageSrcSet } from "../utils/imageUrl";
import "./CategoryNav.css";

function CategoryNav({
  categories,
  activeCategory,
  onCategorySelect,
}) {
  const { t } = useTranslation();
  const navRef = useRef(null);
  const activeRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const tolerance = 2;
    setCanScrollLeft(nav.scrollLeft > tolerance);
    setCanScrollRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - tolerance);
  }, []);

  // Update indicators on scroll, resize, and category changes
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    updateScrollIndicators();
    nav.addEventListener("scroll", updateScrollIndicators, { passive: true });
    window.addEventListener("resize", updateScrollIndicators);

    return () => {
      nav.removeEventListener("scroll", updateScrollIndicators);
      window.removeEventListener("resize", updateScrollIndicators);
    };
  }, [updateScrollIndicators, categories]);

  // Scroll active category into view
  useEffect(() => {
    if (activeRef.current && navRef.current) {
      const nav = navRef.current;
      const active = activeRef.current;
      const navRect = nav.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();

      // Center the active item if it's not fully visible
      if (activeRect.left < navRect.left || activeRect.right > navRect.right) {
        const scrollLeft =
          active.offsetLeft - nav.offsetWidth / 2 + active.offsetWidth / 2;
        nav.scrollTo({ left: scrollLeft, behavior: "smooth" });
      }
    }
  }, [activeCategory]);

  const handleCategoryClick = (categoryId) => {
    onCategorySelect(categoryId);
  };

  const navClasses = [
    "category-nav",
    canScrollLeft && "category-nav--scroll-left",
    canScrollRight && "category-nav--scroll-right",
  ].filter(Boolean).join(" ");

  return (
    <nav
      className={navClasses}
      ref={navRef}
      aria-label={t("nav.categories")}
    >
      <div className="category-nav-inner">
        {/* All items option */}
        <button
          ref={activeCategory === null ? activeRef : null}
          className={`category-item ${activeCategory === null ? "active" : ""}`}
          onClick={() => handleCategoryClick(null)}
        >
          <div className="category-image-wrapper">
            <span className="material-icons category-icon-placeholder" aria-hidden="true">
              restaurant_menu
            </span>
          </div>
          <span className="category-item-label">{t("menu.allItems")}</span>
        </button>

        {/* Category items */}
        {categories.map((category) => (
          <button
            key={category.categoryId}
            ref={activeCategory === category.categoryId ? activeRef : null}
            className={`category-item ${activeCategory === category.categoryId ? "active" : ""}`}
            onClick={() => handleCategoryClick(category.categoryId)}
          >
            <div className="category-image-wrapper">
              {category.imageUrl ? (
                <img
                  src={getOptimizedImageUrl(category.imageUrl, { width: 64 })}
                  srcSet={getImageSrcSet(category.imageUrl, 64)}
                  sizes="44px"
                  alt={category.name}
                  className="category-image"
                  width={44}
                  height={44}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="material-icons category-icon-placeholder" aria-hidden="true">
                  category
                </span>
              )}
            </div>
            <span className="category-item-label">{category.name}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

export default CategoryNav;
